import { Box, Divider, Tag, Text } from '@wix/design-system';
import { useTranslation } from '@wix/wix-i18n-config';
import { FunctionComponent } from 'react';
import {
  EDITORS_NAMES,
  EXPERIMENTS,
  ROADMAP_RESOLUTIONS,
} from '../../../constants';
import { DATA_HOOKS } from '../../../dataHooks';
import { ArticleSearchResultItem } from '../../../types';
import { COLORS } from '../../../utils/theme';
import { InnerLink } from '../../InnerLink';
import css from './index.module.scss';
import { useExperiments } from '@wix/fe-essentials-standalone';
import { RenderHTML } from '../../RenderHTML';

export type SearchBarResultsProps = {
  articles: ArticleSearchResultItem[];
  inputText: string;
  focusedArticleIndex: number;
  onSearchResultClick: (
    article: ArticleSearchResultItem,
    index: number
  ) => Promise<void>;
  onClickAllResults: () => Promise<void>;
  setFocusedArticleIndex: (index: number) => void;
};

export const SearchBarResults: FunctionComponent<SearchBarResultsProps> = ({
  articles,
  inputText,
  focusedArticleIndex,
  onSearchResultClick,
  onClickAllResults,
  setFocusedArticleIndex,
}) => {
  const { t } = useTranslation();
  const { experiments } = useExperiments({ readOnly: true });

  const isDecodeDescriptionEnabled = experiments.enabled(
    EXPERIMENTS.SPECS.SEARCH_RESULTS_DESCRIPTION_PARSE_AS_HTML
  );

  const getArticleTitle = (title: string) => {
    if (title.includes(':')) {
      const [firstPart, secondPart] = title.split(':');
      const isEditor = EDITORS_NAMES.some((editor) =>
        firstPart.includes(editor)
      );
      if (isEditor) {
        return (
          <>
            <b>{firstPart}</b>:{secondPart}
          </>
        );
      }
    }
    return title;
  };

  const getArticleTagLabel = (article: ArticleSearchResultItem) => {
    if (
      article.resolution &&
      ROADMAP_RESOLUTIONS.includes(article.resolution)
    ) {
      return t('search-bar-results.type.roadmap');
    } else {
      return t(`search-bar-results.type.${article.type}`);
    }
  };

  const tooltipProps = {
    enterDelay: 250,
  };

  return (
    <Box
      display={inputText && articles.length > 0 ? 'block' : 'none'}
      className={css.searchBarResults}
      dataHook={DATA_HOOKS.SEARCH_BAR_RESULTS}
      direction="vertical"
    >
      <Box className={css.searchBarResultsList} direction="vertical">
        {articles.map((article, index) => (
          <InnerLink
            key={article.id}
            href={article.uri}
            onClick={async () => {
              await onSearchResultClick(article, index);
            }}
            dataHook={DATA_HOOKS.SEARCH_BAR_RESULT}
            className={`${css.searchBarResult}
            ${focusedArticleIndex === index ? css.focused : ''}`}
            onMouseEnter={() => setFocusedArticleIndex(index)}
          >
            <Box className={css.searchBarResultContent} verticalAlign="middle">
              <Box direction="vertical" className={css.searchBarResultText}>
                <Text
                  size="medium"
                  weight="thin"
                  ellipsis
                  tooltipProps={tooltipProps}
                  className={css.searchBarResultTitle}
                  dataHook={DATA_HOOKS.SEARCH_BAR_RESULT_TITLE}
                >
                  {getArticleTitle(article.title)}
                </Text>
                {isDecodeDescriptionEnabled ? (
                  <RenderHTML
                    html={article.description}
                    textElement={{
                      className: css.searchBarResultDescription,
                      weight: 'thin',
                      size: 'small',
                      dataHook: DATA_HOOKS.SEARCH_BAR_RESULT_DESCRIPTION,
                      ellipsis: true,
                      tooltipProps,
                    }}
                  />
                ) : (
                  <Text
                    className={css.searchBarResultDescription}
                    color={COLORS.D40}
                    weight="thin"
                    size="small"
                    dataHook={DATA_HOOKS.SEARCH_BAR_RESULT_DESCRIPTION}
                    ellipsis
                    tooltipProps={tooltipProps}
                  >
                    {article.description}
                  </Text>
                )}
              </Box>
              <Tag
                id={article.id}
                size="tiny"
                removable={false}
                theme="lightOutlined"
              >
                {getArticleTagLabel(article)}
              </Tag>
            </Box>
          </InnerLink>
        ))}
      </Box>
      <Divider />
      <Box
        className={`${css.searchBarResultsFooter} ${
          focusedArticleIndex === articles.length ? css.focused : ''
        }`}
        direction="vertical"
        align="left"
      >
        <InnerLink
          href={`/search?term=${inputText}`}
          className={css.seeMoreResults}
          dataHook={DATA_HOOKS.SHOW_ALL_SEARCH_RESULTS}
          onClick={onClickAllResults}
          onMouseEnter={() => setFocusedArticleIndex(articles.length)}
        >
          <Text size="medium" weight="thin" className={css.seeMoreResultsText}>
            {t('searchbar.see.all.results')} ‘{inputText}’
          </Text>
        </InnerLink>
      </Box>
      <div aria-live="assertive" key={inputText} className={css.srOnly}>
        {t(`searchbar.results.reader`, { count: articles.length })}
      </div>
    </Box>
  );
};
